import { useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus, Search } from 'lucide-react'
import { formatearFecha } from '@/lib/fecha'
import { digitoVerificadorCorrecto, enmascararMientrasEscribe, esRNCValido, formatearRNC, limpiarRNC } from '@/lib/rnc'
import { Boton } from '@/ui/Boton'
import { Banner } from '@/ui/Banner'
import { CampoFormulario, CampoTexto, Selector } from '@/ui/CampoFormulario'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { RNC } from '@/ui/Numero'
import { PanelLateral } from '@/ui/PanelLateral'
import { TablaDatos } from '@/ui/tabla/TablaDatos'
import { TAMANOS_PAGINA } from '@/ui/tabla/Controles'
import { MOTIVO_MAXIMO, MOTIVO_MINIMO } from '@/consola/DialogoConMotivo'
import {
  AMBIENTES,
  ESTADOS_TENANT,
  nombreAmbiente,
  nombreMotor,
  presentacionEstadoTenant,
  tonoVencimiento,
  type Ambiente,
  type TenantFila,
} from '@/dominio/consola/tipos'
import { crearTenant, traducirErrorConsola, useRefrescarListado, useTenants, type ErrorTraducido } from '@/dominio/consola/useConsola'
import { EncabezadoPantalla } from '../../-pantalla'
import { diasHasta, entero, useEsDueno } from '../-comun'

/**
 * Clientes de la plataforma (`docs/09` §5.1).
 *
 * Son más de 13 000 en desarrollo, así que **todo lo decide el servidor**:
 * búsqueda, filtro por estado y página. La búsqueda espera a que se deje de
 * teclear para no disparar una consulta por letra, y los filtros viven en la
 * URL para que un listado se pueda pasar por enlace.
 */

interface BusquedaClientes {
  buscar?: string
  estado?: string
  pagina?: number
  tamano?: number
}

function numeroPositivo(valor: unknown): number | undefined {
  const numero = typeof valor === 'number' ? valor : typeof valor === 'string' ? Number(valor) : NaN
  return Number.isInteger(numero) && numero > 0 ? numero : undefined
}

export const Route = createFileRoute('/consola/clientes/')({
  validateSearch: (busqueda: Record<string, unknown>): BusquedaClientes => ({
    buscar: typeof busqueda.buscar === 'string' && busqueda.buscar !== '' ? busqueda.buscar : undefined,
    estado: typeof busqueda.estado === 'string' && busqueda.estado !== '' ? busqueda.estado : undefined,
    pagina: numeroPositivo(busqueda.pagina),
    tamano: numeroPositivo(busqueda.tamano),
  }),
  component: PantallaClientes,
})

/** Lo que tarda en dispararse la búsqueda desde la última tecla. */
const RETARDO_BUSQUEDA_MS = 300

function PantallaClientes() {
  const busqueda = Route.useSearch()
  const navegar = useNavigate({ from: Route.fullPath })
  const dueno = useEsDueno()
  const [altaAbierta, setAltaAbierta] = useState(false)

  const pagina = busqueda.pagina ?? 1
  const tamano = TAMANOS_PAGINA.includes(busqueda.tamano as (typeof TAMANOS_PAGINA)[number]) ? busqueda.tamano! : 50

  // El texto del campo es local e inmediato; el de la URL, con retardo. Si el
  // campo leyera de la URL, cada tecla esperaría a la navegación.
  const [texto, setTexto] = useState(busqueda.buscar ?? '')
  const ultimoAplicado = useRef(busqueda.buscar ?? '')

  useEffect(() => {
    // Si la URL cambia por fuera (atrás, un enlace), el campo la sigue.
    if ((busqueda.buscar ?? '') !== ultimoAplicado.current) {
      ultimoAplicado.current = busqueda.buscar ?? ''
      setTexto(busqueda.buscar ?? '')
    }
  }, [busqueda.buscar])

  useEffect(() => {
    const limpio = texto.trim()
    if (limpio === ultimoAplicado.current) return
    const temporizador = setTimeout(() => {
      ultimoAplicado.current = limpio
      void navegar({
        search: (previo) => ({ ...previo, buscar: limpio === '' ? undefined : limpio, pagina: undefined }),
        replace: true,
      })
    }, RETARDO_BUSQUEDA_MS)
    return () => clearTimeout(temporizador)
  }, [texto, navegar])

  const consulta = useTenants({
    // Un RNC se busca sin guiones, también a medio teclear: el servidor guarda
    // los dígitos pelados y «1-31» no coincidiría con nada.
    buscar:
      busqueda.buscar !== undefined && /^[\d\s-]+$/.test(busqueda.buscar)
        ? limpiarRNC(busqueda.buscar)
        : busqueda.buscar,
    estado: busqueda.estado,
    limite: tamano,
    saltar: (pagina - 1) * tamano,
  })

  const hayFiltros = busqueda.buscar !== undefined || busqueda.estado !== undefined

  const columnas = useMemo<ColumnDef<TenantFila, unknown>[]>(
    () => [
      {
        id: 'razonSocial',
        accessorFn: (f) => f.razonSocial,
        header: 'Cliente',
        enableSorting: false,
        meta: { etiqueta: 'Cliente', exportar: (f) => f.razonSocial },
        // En una línea, con el nombre comercial detrás: la fila mide lo mismo
        // tenga nombre comercial o no.
        cell: ({ row }) => (
          <span
            className="block truncate"
            title={[row.original.razonSocial, row.original.nombreComercial].filter(Boolean).join(' · ')}
          >
            <span className="text-gris-900">{row.original.razonSocial}</span>
            {row.original.nombreComercial ? (
              <span className="text-etiqueta text-gris-600"> · {row.original.nombreComercial}</span>
            ) : null}
          </span>
        ),
      },
      {
        id: 'rnc',
        accessorFn: (f) => f.rnc,
        header: 'RNC',
        enableSorting: false,
        meta: { etiqueta: 'RNC', numerico: true, ancho: '120px', exportar: (f) => formatearRNC(f.rnc) },
        cell: ({ row }) => <RNC valor={row.original.rnc} />,
      },
      {
        id: 'estado',
        accessorFn: (f) => f.estado,
        header: 'Estado',
        enableSorting: false,
        meta: {
          etiqueta: 'Estado',
          ancho: '165px',
          exportar: (f) => `${presentacionEstadoTenant(f.estado).etiqueta} · ${nombreAmbiente(f.ambiente)}`,
        },
        // El ambiente va como aclaración del estado y no en columna propia: con
        // todas las columnas sueltas, a 1280 px el nombre del cliente se queda
        // sin sitio, y el nombre es lo que se busca con la vista.
        cell: ({ row }) => {
          const p = presentacionEstadoTenant(row.original.estado)
          return (
            <InsigniaEstado tono={p.tono} aclaracion={nombreAmbiente(row.original.ambiente).toLowerCase()}>
              {p.etiqueta}
            </InsigniaEstado>
          )
        },
      },
      {
        id: 'motor',
        accessorFn: (f) => f.motor ?? '',
        header: 'Motor',
        enableSorting: false,
        meta: { etiqueta: 'Motor', ancho: '130px', exportar: (f) => nombreMotor(f.motor, f.proveedor) },
        cell: ({ row }) => (
          <span className="block truncate text-gris-700" title={nombreMotor(row.original.motor, row.original.proveedor)}>
            {nombreMotor(row.original.motor, row.original.proveedor)}
          </span>
        ),
      },
      {
        id: 'usuarios',
        accessorFn: (f) => f.usuariosActivos,
        header: 'Usuarios',
        enableSorting: false,
        meta: { etiqueta: 'Usuarios activos', numerico: true, ancho: '75px' },
        cell: ({ row }) => entero(row.original.usuariosActivos),
      },
      {
        id: 'documentos',
        accessorFn: (f) => f.documentos30Dias,
        header: 'Docs 30 d',
        enableSorting: false,
        meta: { etiqueta: 'Documentos en 30 días', numerico: true, ancho: '85px' },
        cell: ({ row }) => entero(row.original.documentos30Dias),
      },
      {
        id: 'atencion',
        accessorFn: (f) => atencionDe(f).map((a) => a.texto).join(' · '),
        header: 'Atención',
        enableSorting: false,
        meta: {
          etiqueta: 'Requiere atención',
          ancho: '210px',
          exportar: (f) => atencionDe(f).map((a) => a.texto).join(' · '),
        },
        cell: ({ row }) => {
          const avisos = atencionDe(row.original)
          if (avisos.length === 0) return <span className="text-gris-400">—</span>
          // Una línea siempre: la fila mide lo mismo con uno que con tres avisos.
          // El primero es el más grave (el orden de `atencionDe`); el resto, en
          // el `title` y en la ficha.
          const [primero, ...resto] = avisos
          return (
            <span className="flex min-w-0 items-center gap-2 overflow-hidden" title={avisos.map((a) => a.texto).join(' · ')}>
              <InsigniaEstado tono={primero!.tono} className="min-w-0 truncate text-etiqueta">
                {primero!.texto}
              </InsigniaEstado>
              {resto.length > 0 ? (
                <span className="shrink-0 text-etiqueta text-gris-600">+{resto.length}</span>
              ) : null}
            </span>
          )
        },
      },
    ],
    [],
  )

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <EncabezadoPantalla
        titulo="Clientes"
        acciones={
          dueno ? (
            <Boton variante="primario" icono={<Plus />} onClick={() => setAltaAbierta(true)}>
              Nuevo cliente
            </Boton>
          ) : null
        }
      />

      <TablaDatos<TenantFila>
        idTabla="consola.clientes"
        columnas={columnas}
        datos={consulta.data?.filas ?? []}
        obtenerId={(f) => f.id}
        cargando={consulta.isLoading}
        error={consulta.isError ? traducirErrorConsola(consulta.error, 'cargar los clientes') : null}
        onReintentar={() => void consulta.refetch()}
        vacio={{ variante: 'primera-vez', mensaje: 'Todavía no hay clientes en la plataforma.' }}
        vacioFiltrado={{
          variante: 'sin-resultados',
          mensaje: describirFiltroVacio(busqueda.buscar, busqueda.estado),
        }}
        hayFiltrosActivos={hayFiltros}
        onLimpiarFiltros={() => {
          ultimoAplicado.current = ''
          setTexto('')
          void navegar({ search: {}, replace: true })
        }}
        paginacion={{ pagina, tamano, total: consulta.data?.total ?? 0 }}
        onPaginacionChange={(siguiente) => {
          void navegar({
            search: (previo) => ({
              ...previo,
              pagina: siguiente.pagina === 1 ? undefined : siguiente.pagina,
              tamano: siguiente.tamano === 50 ? undefined : siguiente.tamano,
            }),
          })
        }}
        onFilaClick={(fila) => void navegar({ to: '/consola/clientes/$id', params: { id: fila.id } })}
        nombreExportacion="clientes-plataforma"
        filtros={
          <>
            <label className="relative flex items-center">
              <span className="solo-lectores">Buscar por nombre o RNC</span>
              <Search aria-hidden="true" className="pointer-events-none absolute left-2 size-4 text-gris-600" />
              <input
                type="search"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder="Nombre o RNC"
                autoFocus
                className="h-[var(--alto-control-menor)] w-[260px] rounded-md border border-gris-500 bg-white pl-7 pr-2 text-etiqueta text-gris-900"
              />
            </label>
            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              Estado
              <select
                value={busqueda.estado ?? ''}
                onChange={(e) =>
                  void navegar({
                    search: (previo) => ({ ...previo, estado: e.target.value === '' ? undefined : e.target.value, pagina: undefined }),
                    replace: true,
                  })
                }
                className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta text-gris-900"
              >
                <option value="">Todos</option>
                {ESTADOS_TENANT.map((e) => (
                  <option key={e.valor} value={e.valor}>
                    {e.etiqueta}
                  </option>
                ))}
              </select>
            </label>
            {consulta.isFetching && !consulta.isLoading ? (
              <span className="text-etiqueta text-gris-600" aria-live="polite">
                Buscando…
              </span>
            ) : null}
          </>
        }
      />

      <AltaDeCliente
        abierto={altaAbierta}
        onCerrar={() => setAltaAbierta(false)}
        onCreado={(id) => {
          setAltaAbierta(false)
          void navegar({ to: '/consola/clientes/$id', params: { id } })
        }}
      />
    </div>
  )
}

/**
 * Lo que pide intervención, en una columna. **Solo lo accionable**: un
 * certificado que vence en diez meses no es un aviso, es un dato, y está en la
 * ficha. Con cuatro columnas de estado separadas, 13 000 filas de «0 · —»
 * esconden las tres que importan.
 *
 * Los cortes del certificado son los del portal: ámbar bajo 60 días, rojo
 * bajo 7 (`docs/07`, Certificado).
 */
function atencionDe(f: TenantFila): { texto: string; tono: 'rojo' | 'ambar' }[] {
  const avisos: { texto: string; tono: 'rojo' | 'ambar' }[] = []
  // Primero lo que impide firmar; después lo que se quedó sin transmitir.
  if (!f.certificadoVenceEl) {
    avisos.push({ texto: 'sin certificado', tono: 'rojo' })
  } else {
    const dias = diasHasta(f.certificadoVenceEl)
    if (dias !== null && dias < 0) {
      avisos.push({ texto: `certificado vencido el ${formatearFecha(f.certificadoVenceEl)}`, tono: 'rojo' })
    } else if (dias !== null && dias < 60) {
      avisos.push({
        texto: `certificado vence en ${dias} d`,
        tono: tonoVencimiento(dias) === 'rojo' ? 'rojo' : 'ambar',
      })
    }
  }
  if (f.trabajosFallidos > 0) {
    avisos.push({
      texto: `${entero(f.trabajosFallidos)} ${f.trabajosFallidos === 1 ? 'fallido' : 'fallidos'}`,
      tono: 'rojo',
    })
  }
  if (f.contingenciaVigente) avisos.push({ texto: 'en contingencia', tono: 'ambar' })
  // El rojo delante: es el que sale en la celda cuando no caben todos.
  return [...avisos.filter((a) => a.tono === 'rojo'), ...avisos.filter((a) => a.tono === 'ambar')]
}

function describirFiltroVacio(buscar: string | undefined, estado: string | undefined): string {
  const partes: string[] = []
  if (buscar) partes.push(`que coincida con «${buscar}»`)
  if (estado) partes.push(`en estado ${presentacionEstadoTenant(estado).etiqueta.toLowerCase()}`)
  return `Ningún cliente ${partes.join(' y ')}.`
}

/* ---------------------------------------------------------------------- */
/* Alta                                                                    */
/* ---------------------------------------------------------------------- */

const CREDENCIAL_MINIMA = 12

/**
 * Alta de cliente en panel lateral, con el listado visible detrás.
 *
 * El RNC se valida por **estructura** (9 u 11 dígitos) y el dígito verificador
 * solo **avisa** (regla 8 de `CLAUDE.md`): hay contribuyentes activos cuyo
 * número no pasa el algoritmo, y quien manda es el directorio de DGII. El
 * servidor lo valida con `Rnc.TryParseDeTercero`.
 */
function AltaDeCliente({
  abierto,
  onCerrar,
  onCreado,
}: {
  abierto: boolean
  onCerrar: () => void
  onCreado: (id: string) => void
}) {
  const refrescar = useRefrescarListado()
  const [creando, setCreando] = useState(false)
  const [rnc, setRnc] = useState('')
  const [razonSocial, setRazonSocial] = useState('')
  const [nombreComercial, setNombreComercial] = useState('')
  const [ambiente, setAmbiente] = useState<Ambiente>('pruebas')
  const [estado, setEstado] = useState<'prueba' | 'activo'>('prueba')
  const [email, setEmail] = useState('')
  const [credencial, setCredencial] = useState('')
  const [motivo, setMotivo] = useState('')
  const [intentado, setIntentado] = useState(false)
  const [error, setError] = useState<ErrorTraducido | null>(null)

  useEffect(() => {
    if (abierto) return
    setRnc('')
    setRazonSocial('')
    setNombreComercial('')
    setAmbiente('pruebas')
    setEstado('prueba')
    setEmail('')
    setCredencial('')
    setMotivo('')
    setIntentado(false)
    setError(null)
  }, [abierto])

  const rncLimpio = limpiarRNC(rnc)
  const errorRnc = !esRNCValido(rncLimpio) ? 'El RNC tiene 9 dígitos y la cédula 11.' : undefined
  const avisoRnc =
    errorRnc === undefined && digitoVerificadorCorrecto(rncLimpio) === false
      ? 'El dígito verificador no cuadra. Hay contribuyentes activos así: si DGII lo tiene registrado, sigue.'
      : undefined
  const errorRazon = razonSocial.trim() === '' ? 'Obligatoria.' : undefined
  const errorEmail = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? 'Un correo válido.' : undefined
  const errorCredencial =
    credencial !== '' && credencial.length < CREDENCIAL_MINIMA ? `Mínimo ${CREDENCIAL_MINIMA} caracteres.` : undefined
  const largoMotivo = motivo.trim().length
  const errorMotivo =
    largoMotivo < MOTIVO_MINIMO || largoMotivo > MOTIVO_MAXIMO
      ? `Entre ${MOTIVO_MINIMO} y ${MOTIVO_MAXIMO} caracteres.`
      : undefined

  const valido = [errorRnc, errorRazon, errorEmail, errorCredencial, errorMotivo].every((e) => e === undefined)

  const enviar = async (): Promise<void> => {
    setIntentado(true)
    if (!valido || creando) return
    setError(null)
    setCreando(true)
    // La credencial inicial no se queda en el formulario ni tras un fallo: se
    // vuelve a teclear. Es de otra persona y no tiene por qué seguir en memoria.
    const inicial = credencial
    setCredencial('')
    try {
      const creado = await crearTenant(
        {
          rnc: rncLimpio,
          razonSocial: razonSocial.trim(),
          nombreComercial: nombreComercial.trim() === '' ? undefined : nombreComercial.trim(),
          ambiente,
          estado,
          administrador: {
            email: email.trim(),
            credencialInicial: inicial === '' ? undefined : inicial,
          },
        },
        motivo.trim(),
      )
      await refrescar()
      onCreado(creado.id)
    } catch (fallo) {
      setError(traducirErrorConsola(fallo, 'dar de alta el cliente'))
    } finally {
      setCreando(false)
    }
  }

  return (
    <PanelLateral
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Nuevo cliente"
      subtitulo="Se crea con su administrador. El certificado y las secuencias se cargan después, en su ficha."
      pie={
        <>
          <Boton variante="secundario" onClick={onCerrar} disabled={creando}>
            Cancelar
          </Boton>
          <Boton variante="primario" onClick={() => void enviar()} disabled={creando}>
            {creando ? 'Creando…' : 'Crear cliente'}
          </Boton>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          void enviar()
        }}
      >
        {error !== null ? (
          <Banner tono="rojo" titulo={error.titulo} detalleTecnico={error.detalleTecnico}>
            {error.contexto}
          </Banner>
        ) : null}

        <CampoTexto
          etiqueta="RNC o cédula"
          obligatorio
          numerico
          inputMode="numeric"
          value={rnc}
          onChange={(e) => setRnc(enmascararMientrasEscribe(e.target.value))}
          error={intentado ? errorRnc : undefined}
          ayuda={avisoRnc ?? 'Se guarda sin guiones. El RNC y el ambiente son la identidad del cliente: no se editan después.'}
        />
        {avisoRnc !== undefined ? (
          <Banner tono="ambar" titulo="Revisa el número">
            {avisoRnc}
          </Banner>
        ) : null}

        <CampoTexto
          etiqueta="Razón social"
          obligatorio
          value={razonSocial}
          onChange={(e) => setRazonSocial(e.target.value)}
          error={intentado ? errorRazon : undefined}
        />
        <CampoTexto
          etiqueta="Nombre comercial"
          anotacion="opcional"
          value={nombreComercial}
          onChange={(e) => setNombreComercial(e.target.value)}
        />

        <div className="grid grid-cols-2 gap-4">
          <CampoFormulario etiqueta="Ambiente" obligatorio ayuda="Contra qué ambiente de DGII emite.">
            {(atributos) => (
              <Selector {...atributos} value={ambiente} onChange={(e) => setAmbiente(e.target.value as Ambiente)}>
                {AMBIENTES.map((a) => (
                  <option key={a.valor} value={a.valor}>
                    {a.etiqueta}
                  </option>
                ))}
              </Selector>
            )}
          </CampoFormulario>
          <CampoFormulario etiqueta="Estado inicial" obligatorio>
            {(atributos) => (
              <Selector
                {...atributos}
                value={estado}
                onChange={(e) => setEstado(e.target.value as 'prueba' | 'activo')}
              >
                <option value="prueba">En prueba</option>
                <option value="activo">Activo</option>
              </Selector>
            )}
          </CampoFormulario>
        </div>

        <fieldset className="flex flex-col gap-4 rounded-md border border-gris-300 p-3">
          <legend className="px-1 text-etiqueta font-medium text-gris-600">Administrador del cliente</legend>
          <CampoTexto
            etiqueta="Correo"
            obligatorio
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={intentado ? errorEmail : undefined}
            ayuda="Si el correo ya tiene cuenta, se le da acceso como administrador y conserva su credencial."
          />
          <CampoTexto
            etiqueta="Credencial inicial"
            anotacion="solo si la cuenta es nueva"
            type="password"
            autoComplete="new-password"
            value={credencial}
            onChange={(e) => setCredencial(e.target.value)}
            error={intentado ? errorCredencial : undefined}
            ayuda={`Mínimo ${CREDENCIAL_MINIMA} caracteres. No se guarda en esta pantalla ni se vuelve a mostrar.`}
          />
        </fieldset>

        <CampoFormulario
          etiqueta="Motivo"
          obligatorio
          anotacion={`${largoMotivo} / ${MOTIVO_MAXIMO}`}
          error={intentado ? errorMotivo : undefined}
          ayuda="Queda en la auditoría de la consola."
        >
          {(atributos) => (
            <textarea
              {...atributos}
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="w-full rounded-md border border-gris-500 bg-white px-2 py-1.5 text-cuerpo text-gris-900 hover:border-gris-600"
            />
          )}
        </CampoFormulario>

        {/* Enter en un campo envía; el botón del pie hace lo mismo. */}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </PanelLateral>
  )
}
